import React, { useState } from 'react';
import axios, { AxiosError } from 'axios';
import FileUpload from 'components/FileUpload/FileUpload';
import RfiReadOnlyAttachments from 'components/RfiReadOnlyAttachments';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useConfirmModal from 'hooks/useConfirmModal';
import { cloneDeep, isEqual } from 'lodash';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectAppConfig } from 'store/appConfigSlice';
import { ConfirmModalProps } from 'store/confirmModalSlice';
import { showErrorMessage } from 'store/errorModalSlice';
import { selectRfi, setElementValues } from 'store/rfiSlice';
import { selectVendorResponse } from 'store/vendorResponseSlice';
import { Button } from '@progress/kendo-react-buttons';
import { InputSeparator, InputSuffix, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Hint } from '@progress/kendo-react-labels';
import { Attachment, AttachmentType, Element, ElementAnswer, elementTypes, IElementProps } from '../../types/RfiTypes';
import { MAX_LENGTH_OPTION } from './ElementEditor';

export interface AttachmentElementProps {
  element: Element;
  readOnly?: boolean;
  questionNumber?: number;
  showValue?: boolean;
  designMode?: boolean;
}

const RfiAttachmentElement = (props: IElementProps) => {
  // Hooks
  const dispatch = useAppDispatch();
  const rfi = useAppSelector(selectRfi);
  const vendorResponse = useAppSelector(selectVendorResponse);
  const appConfig = useAppSelector(selectAppConfig);
  const { open } = useConfirmModal();

  // State
  const [localAttachments, setLocalAttachments] = useState<Array<Attachment>>(
    () => {
      let result = new Array<Attachment>();
      if (props.element?.answers) {
        props.element.answers.map((answer: ElementAnswer) => {
          if (answer.attachment) {
            result.push(answer.attachment);
          }
        });
      }
      return result;
    }
  );

  // Handlers
  // Get ElementAnswers/Update State if attachments change
  const onUpdateAttachments = () => {
    axios
      .get(`/api/v1/elements/rfi/${props.element.elementId}`, {
        params: {
          vendorResponseId: vendorResponse?.vendorResponse?.vendorResponseId,
        },
      })
      .then((result) => {
        if (result.data) {
          dispatch(
            setElementValues({
              elementId: props.element.elementId,
              values: result.data.answers ?? ([] as ElementAnswer[]),
            })
          );
        }
      })
      .catch((error) => {
        console.log(error);
        dispatch(
          showErrorMessage(
            `There was an error getting uploaded attachments - ${error}. Please refresh the page.`
          )
        );
      });
  };

  const handleUploadFinish = (attachments: Attachment[]) => {
    if (attachments) {
      setLocalAttachments(attachments);
      onUpdateAttachments();
    }
  };

  const handleUpdateTitleValue = (event: TextBoxChangeEvent, id: number) => {
    setLocalAttachments(
      localAttachments.map((attachment: Attachment, oldIndex: number) => {
        if (attachment.id === id) {
          attachment.title = event.target.value?.toString();
        }
        return attachment;
      })
    );
  };

  const handleUpdateTitle = async (
    attachmentId: number,
    elementId: number,
    value: string
  ) => {
    return axios
      .put(`/api/v1/rfi/${rfi?.rfi?.rfiId ?? 0}/Attachments`, {
        id: attachmentId,
        rfiId: rfi?.rfi?.rfiId ?? 0,
        //vendorResponseId: vendorResponse?.vendorResponse?.vendorResponseId ?? 0,
        elementId: elementId,
        title: value,
        attachmentType: AttachmentType.ELEMENT,
      })
      .then(() => {
        onUpdateAttachments();
        toast.success("Updated");
      })
      .catch((error: Error | AxiosError) => {
        console.log(error);
        dispatch(
          showErrorMessage(`There was an error updating file title - ${error}`)
        );
      });
  };

  const swapAttachments = (index1: number, index2: number) => {
    var att = cloneDeep(localAttachments);
    const sequence1 = att[index1].sequence;
    const sequence2 = att[index2].sequence;

    var temp = att[index1];

    //swap values and reset sequences
    att[index1] = att[index2];
    att[index1].sequence = sequence1;

    att[index2] = temp;
    att[index2].sequence = sequence2;

    return att;
  };

  const handleMoveFile = async (sequence: number, direction: number) => {
    let moveParams = {
      sequence: sequence,
      direction: direction,
      rfiId: rfi?.rfi?.rfiId ?? 0,
      vendorResponseId: vendorResponse?.vendorResponse?.vendorResponseId ?? 0,
      elementId: props.element.elementId ?? 0,
      attachmentType: AttachmentType.ELEMENT,
    };

    return axios
      .put(`/api/v1/rfi/${rfi?.rfi?.rfiId ?? 0}/Attachments/move`, moveParams)
      .then(() => {
        onUpdateAttachments();
      })
      .catch((error: Error | AxiosError) => {
        console.log(error);
        dispatch(
          showErrorMessage(`There was an error updating file title - ${error}`)
        );
      });
  };

  const handleDeleteFile = async (attachmentId: number) => {
    const isConfirmed = await open({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this attachment?",
      description: "Confirm delete attachment",
    } as ConfirmModalProps);

    if (isConfirmed) {
      await axios
        .delete(
          `/api/v1/rfi/${rfi?.rfi?.rfiId ?? 0}/Attachments/${attachmentId}`,
          {
            params: {
              elementId: props.element.elementId,
              vendorResponseId:
                vendorResponse?.vendorResponse?.vendorResponseId,
            },
          }
        )
        .then(() => {
          const newAtt = localAttachments.filter(
            (file: Attachment) => file.id !== attachmentId
          );
          setLocalAttachments(newAtt);
          onUpdateAttachments();
        })
        .catch((error: Error | AxiosError) => {
          console.log(error);
          dispatch(
            showErrorMessage(
              `There was an error updating file title - ${error}`
            )
          );
        });
    }
  };

  const handleMoveFileDown = async (index: number) => {
    await handleMoveFile(index, 1).then(() => {
      const newAtt = swapAttachments(index - 1, index);
      setLocalAttachments(newAtt);
    });
  };

  const handleMoveFileUp = async (index: number) => {
    await handleMoveFile(index, 0).then(() => {
      const newAtt = swapAttachments(index - 1, index - 2);
      setLocalAttachments(newAtt);
    });
  };

  // Render
  if (props.element.elementType === elementTypes.FIELD) {
    if (props.readOnly) {
      return (
        <>
          <div className="form-group row">
            <div className="col-lg col-form-label">
              {props.element.fieldLabel}
            </div>
          </div>
          <div className="form-group row">
            <div className="col-lg fieldElement">
              <RfiReadOnlyAttachments attachments={localAttachments} />
            </div>
          </div>
        </>
      );
    } else
      return (
        <>
          <div className="form-group row">
            <div className="col-lg col-form-label">
              <label
                className="col-form-label"
                htmlFor={`attachment-${props.element.elementId}`}
              >
                {props.element.fieldLabel}{" "}
                {props.overrideRequired === undefined ? (
                  props.element.requiredResponse ? (
                    <span className="requiredField">(required)</span>
                  ) : (
                    ""
                  )
                ) : props.overrideRequired === true ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )}
              </label>
            </div>
          </div>
          <div className="form-group row">
            <div
              className="col-lg attachmentElement"
              id={`element-upload-${props.element?.elementId ?? 0}`}
            >
              <FileUpload
                attachmentType={AttachmentType.ELEMENT}
                rfiId={rfi?.rfi?.rfiId ?? 0}
                elementId={props.element.elementId}
                elementLabel={props.element.fieldLabel}
                setElementAttachments={handleUploadFinish}
              />
              {/* Attachment List */}
              <div className="container-fluid">
                {localAttachments?.map((file: Attachment, index: number) => {
                  return (
                    <div
                      className="d-flex flex-row flex-nowrap my-1"
                      key={`element-${props.element.elementId}-file-${index}`}
                    >
                      <div
                        className="flex-nowrap"
                        style={{
                          display: "block-inline",
                          width: "55%",
                          marginRight: "10px",
                        }}
                      >
                        <label
                          className="visuallyHidden"
                          htmlFor={`file-${file.id}`}
                        >
                          {`File ${file.id} ${file.title} (${file.filename})`}
                        </label>
                        <TextBox
                          id={`file-${file.id}`}
                          //className="form-control col"
                          type="text"
                          value={file.title ?? ""}
                          placeholder={"Document title"}
                          maxLength={
                            appConfig.maxOptionLength ?? MAX_LENGTH_OPTION
                          }
                          onChange={(event: TextBoxChangeEvent) =>
                            handleUpdateTitleValue(event, file.id)
                          }
                          suffix={() => (
                            <React.Fragment>
                              <InputSeparator />
                              <InputSuffix>
                                <Button
                                  themeColor="primary"
                                  fillMode={"flat"}
                                  rounded={null}
                                  onClick={() =>
                                    handleUpdateTitle(
                                      file.id,
                                      props.element.elementId,
                                      file.title ?? ""
                                    )
                                  }
                                >
                                  Update
                                </Button>
                              </InputSuffix>
                            </React.Fragment>
                          )}
                        />
                        <Hint
                          direction={"end"}
                          className="col-1 align-items-center"
                          style={{ minWidth: "50px" }}
                        >
                          {file.title?.length ?? 0} /{" "}
                          {appConfig.maxOptionLength ?? MAX_LENGTH_OPTION}
                        </Hint>
                      </div>
                      <div style={{ width: "35%", marginRight: "10px" }}>
                        (
                        <Link
                          to={`/api/v1/rfi/${rfi?.rfi?.rfiId}/Attachments/${file.attachmentLink}`}
                          target="_blank"
                          download
                        >
                          {`${file.filename}`}
                        </Link>
                        )
                      </div>
                      <div
                        className="d-flex flex-row flex-nowrap my-1"
                        style={{ width: "100px" }}
                      >
                        {
                          // Don't show Down Arrow on last item

                          <div
                            className="k-mr-2"
                            style={{ textAlign: "center" }}
                          >
                            <button
                              disabled={
                                !(localAttachments.length > 0
                                  ? file !==
                                    localAttachments[
                                      localAttachments.length - 1
                                    ]
                                  : false)
                              }
                              className="text-align-center round-button"
                              aria-label={`Move file ${file.filename} Down`}
                              onClick={() =>
                                handleMoveFileDown(file.sequence ?? 0)
                              }
                            >
                              <i className="fa-solid fa-arrow-down" />
                            </button>
                          </div>
                        }
                        {
                          // Don't show Up Arrow on first item
                          (
                            localAttachments.length > 0
                              ? file !== localAttachments[0]
                              : false
                          ) ? (
                            <div
                              className="k-mr-2"
                              style={{ textAlign: "center" }}
                            >
                              <button
                                className="text-align-center round-button"
                                //style={{ textAlign: "center" }}
                                aria-label={`Move Option ${file.filename} Up`}
                                onClick={() =>
                                  handleMoveFileUp(file.sequence ?? 0)
                                }
                              >
                                <i className="fa-solid fa-arrow-up" />
                              </button>
                            </div>
                          ) : (
                            <div
                              className="k-mr-2"
                              style={{ textAlign: "center" }}
                            >
                              <button
                                disabled
                                className="text-align-center round-button"
                                //style={{ textAlign: "center" }}
                                aria-label={`Move Option ${file.filename} Up`}
                                onClick={() =>
                                  handleMoveFileUp(file.sequence ?? 0)
                                }
                              >
                                <i className="fa-solid fa-arrow-up" />
                              </button>
                            </div>
                          )
                        }
                        <div className="k-mr-2" style={{ textAlign: "center" }}>
                          <button
                            className="align-items-center round-button"
                            aria-label={`Delete file ${file.filename}`}
                            onClick={() => handleDeleteFile(file.id)}
                          >
                            <i className="fa-solid fa-trash" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* END Attachment List */}
            </div>
          </div>
        </>
      );
  }
  // SURVEY QUESTION
  else {
    if (props.readOnly) {
      return (
        <>
          <div className="form-group row">
            <div className="col-form-label">
              {`${props.questionNumber}.`} {props.element.fieldLabel}
            </div>
          </div>
          <div className="form-group row">
            <div style={{ marginLeft: "20px" }}>
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled
                aria-label="file upload button"
              >
                <i
                  className="fa fa-file-upload"
                  style={{ fontSize: "2.0rem" }}
                />
              </button>
            </div>
          </div>
        </>
      );
    } else
      return (
        <>
          <fieldset>
            <div className="form-group row">
              <legend className="visuallyHidden">
                {props.element.fieldLabel}
              </legend>
              <label
                className="col-form-label"
                htmlFor={`attachment-${props.element.elementId}`}
              >
                <div id={`button-${props.element.elementId}`}>
                  {`${props.questionNumber}.`} {props.element.fieldLabel}{" "}
                  {props.overrideRequired === undefined ? (
                    props.element.requiredResponse ? (
                      <span className="requiredField">(required)</span>
                    ) : (
                      ""
                    )
                  ) : props.overrideRequired === true ? (
                    <span className="requiredField">(required)</span>
                  ) : (
                    ""
                  )}
                  <FileUpload
                    attachmentType={AttachmentType.ELEMENT}
                    rfiId={rfi?.rfi?.rfiId ?? 0}
                    vendorResponseId={
                      vendorResponse?.vendorResponse?.vendorResponseId ?? 0
                    }
                    elementId={props.element.elementId}
                    elementLabel={`${props.questionNumber}. ${props.element.fieldLabel}`}
                    setElementAttachments={handleUploadFinish}
                  />
                </div>
              </label>
            </div>
            {/* Attachment List */}
            <div className="form-group row">
              <div className="container-fluid">
                {localAttachments?.map((file: Attachment, index: number) => {
                  return (
                    <div
                      className="d-flex flex-row flex-nowrap my-1"
                      key={`element-${props.element.elementId}-file-${index}`}
                    >
                      <div
                        className="flex-nowrap"
                        style={{
                          display: "block-inline",
                          width: "55%",
                          marginRight: "10px",
                        }}
                      >
                        <label
                          className="visuallyHidden"
                          htmlFor={`file-${file.id}`}
                        >
                          {`File ${file.id} ${file.title} (${file.filename})`}
                        </label>
                        <TextBox
                          id={`file-${file.id}`}
                          //className="form-control col"
                          type="text"
                          value={file.title ?? ""}
                          placeholder={"Document title"}
                          maxLength={
                            appConfig.maxOptionLength ?? MAX_LENGTH_OPTION
                          }
                          onChange={(event: TextBoxChangeEvent) =>
                            handleUpdateTitleValue(event, file.id)
                          }
                          suffix={() => (
                            <React.Fragment>
                              <InputSeparator />
                              <InputSuffix>
                                <Button
                                  themeColor="primary"
                                  fillMode={"flat"}
                                  rounded={null}
                                  onClick={() =>
                                    handleUpdateTitle(
                                      file.id,
                                      props.element.elementId,
                                      file.title ?? ""
                                    )
                                  }
                                >
                                  Update
                                </Button>
                              </InputSuffix>
                            </React.Fragment>
                          )}
                        />
                        <Hint
                          direction={"end"}
                          className="col-1 align-items-center"
                          style={{ minWidth: "50px" }}
                        >
                          {file.title?.length ?? 0} /{" "}
                          {appConfig.maxOptionLength ?? MAX_LENGTH_OPTION}
                        </Hint>
                      </div>
                      <div style={{ width: "35%", marginRight: "10px" }}>
                        (
                        <Link
                          to={`/api/v1/rfi/${rfi?.rfi?.rfiId}/Attachments/${file.attachmentLink}`}
                          target="_blank"
                          download
                        >
                          {`${file.filename}`}
                        </Link>
                        )
                      </div>
                      <div
                        className="d-flex flex-row flex-nowrap my-1"
                        style={{ width: "100px" }}
                      >
                        {
                          // Don't show Down Arrow on last item

                          <div
                            className="k-mr-2"
                            style={{ textAlign: "center" }}
                          >
                            <button
                              disabled={
                                !(localAttachments.length > 0
                                  ? file !==
                                    localAttachments[
                                      localAttachments.length - 1
                                    ]
                                  : false)
                              }
                              className="text-align-center round-button"
                              aria-label={`Move file ${file.filename} Down`}
                              onClick={() =>
                                handleMoveFileDown(file.sequence ?? 0)
                              }
                            >
                              <i className="fa-solid fa-arrow-down" />
                            </button>
                          </div>
                        }
                        {
                          // Don't show Up Arrow on first item
                          (
                            localAttachments.length > 0
                              ? file !== localAttachments[0]
                              : false
                          ) ? (
                            <div
                              className="k-mr-2"
                              style={{ textAlign: "center" }}
                            >
                              <button
                                className="text-align-center round-button"
                                //style={{ textAlign: "center" }}
                                aria-label={`Move Option ${file.filename} Up`}
                                onClick={() =>
                                  handleMoveFileUp(file.sequence ?? 0)
                                }
                              >
                                <i className="fa-solid fa-arrow-up" />
                              </button>
                            </div>
                          ) : (
                            <div
                              className="k-mr-2"
                              style={{ textAlign: "center" }}
                            >
                              <button
                                disabled
                                className="text-align-center round-button"
                                //style={{ textAlign: "center" }}
                                aria-label={`Move Option ${file.filename} Up`}
                                onClick={() =>
                                  handleMoveFileUp(file.sequence ?? 0)
                                }
                              >
                                <i className="fa-solid fa-arrow-up" />
                              </button>
                            </div>
                          )
                        }
                        <div className="k-mr-2" style={{ textAlign: "center" }}>
                          <button
                            className="align-items-center round-button"
                            aria-label={`Delete file ${file.filename}`}
                            onClick={() => handleDeleteFile(file.id)}
                          >
                            <i className="fa-solid fa-trash" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* END Attachment List */}
          </fieldset>
        </>
      );
  }
};

const areEqual = (prevProps: IElementProps, nextProps: IElementProps) => {
  return (
    !prevProps.rerender &&
    isEqual(prevProps.element.elementId, nextProps.element.elementId) &&
    isEqual(prevProps.element, nextProps.element) &&
    isEqual(prevProps.designMode, nextProps.designMode) &&
    isEqual(prevProps.readOnly, nextProps.readOnly)
  );
};

const AttachmentElement = React.memo(RfiAttachmentElement, areEqual);

export default AttachmentElement;
